"use client";

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box, Typography, Button, Modal, IconButton, useTheme } from "@mui/material";
import { useTranslations } from "next-intl";
import React from "react";
import { colors } from "../../theme/colors";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "95%",
    sm: "85%",
    md: "600px"
  },
  bgcolor: colors.background.paper,
  boxShadow: colors.shadows.modal,
  borderRadius: "24px",
  outline: "none",
  overflow: "hidden",
  p: 0
};
export default function FreeSetupCallModal({
  open,
  handleClose
}) {
  const theme = useTheme();
  const t = useTranslations("general"); // Assuming we'll add keys later, using hardcoded for now or general

  // Fallback text if translations are missing
  const title = "Need help setting up?";
  const subtitle = "Book a free 1-on-1 setup call with our Amazon experts to get the most out of Descripio.";
  const bookBtn = "Book Free Call";
  const laterBtn = "Maybe Later";
  return <Modal className="animate__animated animate__fadeIn" open={open} onClose={handleClose} aria-labelledby="setup-modal-title" aria-describedby="setup-modal-description" closeAfterTransition data-sentry-element="Modal" data-sentry-component="FreeSetupCallModal" data-sentry-source-file="FreeSetupCallModal.jsx">
            <Box sx={style} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx">
                {/* Banner Image / Gradient Header */}
                <Box sx={{
        height: "140px",
        background: `linear-gradient(135deg, ${colors.cta.main} 0%, ${colors.cta.hover} 100%)`,
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden"
      }} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx">
                    {/* Decorative Circles */}
                    <Box sx={{
          position: "absolute",
          top: "-50%",
          left: "-20%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          border: `2px solid ${colors.overlay.white15}`
        }} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx" />
                    <Box sx={{
          position: "absolute",
          bottom: "-30%",
          right: "-10%",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${colors.overlay.white15} 0%, transparent 70%)`
        }} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx" />

                    {/* Icon */}
                    <Box sx={{
          width: 80,
          height: 80,
          bgcolor: colors.background.white,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          zIndex: 2
        }} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx">
                        <SupportAgentIcon sx={{
            fontSize: 40,
            color: colors.cta.main
          }} data-sentry-element="SupportAgentIcon" data-sentry-source-file="FreeSetupCallModal.jsx" />
                    </Box>

                    <IconButton onClick={handleClose} sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: colors.background.white,
          bgcolor: colors.overlay.black10,
          "&:hover": {
            bgcolor: colors.overlay.black25
          }
        }} data-sentry-element="IconButton" data-sentry-source-file="FreeSetupCallModal.jsx">
                        <CloseIcon sx={{
            fontSize: "20px",
            width: "20px",
            height: "20px"
          }} data-sentry-element="CloseIcon" data-sentry-source-file="FreeSetupCallModal.jsx" />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{
        p: {
          xs: 3,
          md: 5
        },
        textAlign: "center"
      }} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx">
                    <Typography id="setup-modal-title" variant="h4" sx={{
          fontWeight: 800,
          mb: 2,
          color: colors.text.primary,
          fontSize: {
            xs: "24px",
            md: "28px"
          }
        }} data-sentry-element="Typography" data-sentry-source-file="FreeSetupCallModal.jsx">
                        {title}
                    </Typography>

                    <Typography id="setup-modal-description" sx={{
          color: colors.text.secondary,
          mb: 4,
          fontSize: "16px",
          lineHeight: 1.6,
          maxWidth: "400px",
          mx: "auto"
        }} data-sentry-element="Typography" data-sentry-source-file="FreeSetupCallModal.jsx">
                        {subtitle}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row"
          },
          gap: 2,
          justifyContent: "center"
        }} data-sentry-element="Box" data-sentry-source-file="FreeSetupCallModal.jsx">
                        <Button variant="contained" startIcon={<CalendarMonthIcon />} onClick={() => {
            // Replace with your actual Calendly/booking link
            window.open("https://outlook.office.com/book/FreeSetupandDemo@raab.de/", "_blank");
            handleClose();
          }} sx={{
            bgcolor: colors.cta.main,
            color: colors.background.white,
            borderRadius: "50px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: "700",
            px: 4,
            py: 1.5,
            boxShadow: "0 8px 16px rgba(172, 18, 24, 0.2)",
            transition: "transform 0.2s",
            "&:hover": {
              bgcolor: colors.cta.hover,
              transform: "translateY(-2px)"
            }
          }} data-sentry-element="Button" data-sentry-source-file="FreeSetupCallModal.jsx">
                            {bookBtn}
                        </Button>

                        <Button variant="text" onClick={handleClose} sx={{
            color: colors.text.secondary,
            borderRadius: "50px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: "600",
            px: 3,
            "&:hover": {
              bgcolor: colors.interactive.hover,
              color: colors.text.primary
            }
          }} data-sentry-element="Button" data-sentry-source-file="FreeSetupCallModal.jsx">
                            {laterBtn}
                        </Button>
                    </Box>

                    <Typography variant="caption" sx={{
          display: 'block',
          mt: 4,
          color: colors.text.tertiary,
          fontWeight: 500
        }} data-sentry-element="Typography" data-sentry-source-file="FreeSetupCallModal.jsx">
                        No credit card required. Free 15-minute consultation.
                    </Typography>
                </Box>
            </Box>
        </Modal>;
}